import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TrackByFunction
} from '@angular/core'
import { map, startWith, switchMap, shareReplay } from 'rxjs/operators'
import { ReplaySubject } from 'rxjs'
import { AnalyticsModel } from '@flocasts/experience-service-types'
import { SegmentEvents } from 'src/app/shared/analytics/models/segment-events.model'
import { SegmentService } from 'src/app/shared/analytics/services/segment.service'
import { capitalizeFirstChar } from 'src/app/shared/utility-functions/string.utility'
import { UserAgentService } from 'src/app/singleton-services/user-agent.service'
import { OldTabComponent } from './old-tab.component'

export interface ITab {
  tabTitle: string
  route: string
  active: boolean
  analyticsName?: string // TODO: (FLO-7407) optional until ALL tabs have an analyticsName
  analytics?: AnalyticsModel // Input from BFF
  primaryTab?: boolean
  count?: number
  qp?: string
}

/**
 * @deprecated
 *
 * This component is deprecated and should not be used in new code.
 * Instead, please use the new tabs.component.ts {@link TabsComponent}
 */
@Component({
  selector: 'old-flo-tabs',
  styleUrls: ['./old-tabs.component.scss'],
  templateUrl: './old-tabs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OldTabsComponent implements AfterContentInit {
  constructor(private userAgentService: UserAgentService, private segment: SegmentService) {}

  @ContentChildren(OldTabComponent) tabs: QueryList<OldTabComponent>
  @Input() public limit = 5
  @Output()
  public readonly currentTab: EventEmitter<ITab> = new EventEmitter<ITab>()
  private readonly ngAfterContentInit$ = new ReplaySubject<void>(1)
  public primaryTabs: ReadonlyArray<ITab> = []
  public collapsibleTabs: ReadonlyArray<ITab> = []
  public dropdownExpanded = false
  public dropdownActive = false

  public tabChanges$ = this.ngAfterContentInit$.pipe(
    switchMap(() => this.tabs.changes.pipe(startWith(this.tabs))),
    map((tabs: QueryList<OldTabComponent>) => tabs.toArray()),
    shareReplay(1)
  )

  public tabClass$ = this.tabChanges$.pipe(
    map(tabArray => tabArray.length),
    map(tabLength => (tabLength > 3 ? true : false)),
    shareReplay(1)
  )

  public buildTabList$ = this.tabChanges$.pipe(
    map(tabs => {
      const limit = this.userAgentService.userAgent.isDesktop ? this.limit : 3
      const primaryTabs = tabs.slice(0, limit)
      const collapsibleTabs = limit < tabs.length ? tabs.slice(limit, tabs.length) : []
      if (limit < tabs.length) {
        return {
          dropdownActive: true,
          primaryTabs,
          collapsibleTabs
        }
      } else {
        return {
          dropdownActive: false,
          primaryTabs: tabs.map((tab: ITab) => ({ ...tab, primaryTab: true })),
          collapsibleTabs
        }
      }
    }),
    shareReplay(1)
  )

  /*
   * Need to update tabs when <router-outlet> is used, ex: event-details
   */
  public updateTab(
    tab: ITab,
    event: Event,
    tabs: { primaryTabs: OldTabComponent[]; collapsibleTabs: OldTabComponent[] }
  ): void {
    tabs.primaryTabs?.forEach(t => (t.active = false))
    tabs.collapsibleTabs?.forEach(t => (t.active = false))
    tab.active = true
    this.currentTab.emit(tab)
    this.dropdownActive = !tab.primaryTab
    this.tabs.setDirty()
    event.stopPropagation()
    this.segment.track(SegmentEvents.TAB_CLICKED, {
      name: tab.analyticsName || capitalizeFirstChar(tab.tabTitle)
    })
  }

  ngAfterContentInit(): void {
    this.ngAfterContentInit$.next()
    this.ngAfterContentInit$.complete()
  }

  /**
   * Event that fires when the dropdown opens and closes (triggered by ngb-bootstrap directive)
   * @param isOpened Boolean to indicate if the dropdown is open or closed
   */
  handleOpenChange(isOpened: boolean) {
    // Property used to style the dropdown open/close state
    this.dropdownExpanded = isOpened
  }

  trackByPrimaryTab: TrackByFunction<ITab> = (index: number, item: ITab) => {
    return item.tabTitle
  }

  trackByCollapsibleTab: TrackByFunction<ITab> = (index: number, item: ITab) => {
    return item.tabTitle
  }
}
